import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
    fetchOrgKnotFlags,
    getKnotVisibility,
    getBrokerKnotVisibility,
    getDefaultKnotDashboardType,
    hasAnyKnotTabAccess,
    isDealKnotsAllowedForRole,
    isKnotTabVisible,
    applyRoleIamFlagOverrides,
    resolveKnotDashboardRedirect,
} from '../utils/knotTabAccess';

const KNOT_ROLES = new Set(['buyer', 'supplier', 'broker']);
const NO_KNOT_VISIBILITY = { trade: false, deal: false, openKnots: false };

function resolveOrgUlid(role, ownOrgUlid, orgOverride) {
    return orgOverride || (role === 'broker' ? null : ownOrgUlid);
}

function shouldFetchKnotFlags(role, orgUlid, orgOverride) {
    return KNOT_ROLES.has(role) && !!orgUlid && (role !== 'broker' || !!orgOverride);
}

function resolveKnotTabVisibility({ role, orgOverride, shouldFetchFlags, flags, userData }) {
    if ((role === 'broker' && !orgOverride) || !shouldFetchFlags) {
        return NO_KNOT_VISIBILITY;
    }
    if (role === 'broker') {
        return getBrokerKnotVisibility(flags);
    }
    return getKnotVisibility(flags, userData);
}

async function fetchKnotFlagsForOrg({ shouldFetchFlags, orgUlid, role, fetchGenRef, setLoaded, setFlags }) {
    if (!shouldFetchFlags || !orgUlid) {
        setLoaded(true);
        setFlags(null);
        return;
    }

    const gen = ++fetchGenRef.current;
    setLoaded(false);
    try {
        const f = await fetchOrgKnotFlags(orgUlid, {
            includeOpenKnots: role === 'buyer' || role === 'supplier',
            includeDeal: isDealKnotsAllowedForRole(role),
        });
        if (fetchGenRef.current === gen) {
            setFlags(f);
        }
    } finally {
        if (fetchGenRef.current === gen) {
            setLoaded(true);
        }
    }
}

/**
 * @param {object} userData
 * @param {{ fetchOnMount?: boolean, organizationUlid?: string|null }} [options]
 * organizationUlid: for brokers, pass associated buyer org ULID.
 */
export function useBuyerKnotTabs(userData, { fetchOnMount = true, organizationUlid: orgOverride } = {}) {
    const role = userData?.user_organizations?.[0]?.user_org_role_maps?.[0]?.platform_role;
    const ownOrgUlid =
        userData?.user_organizations?.[0]?.organization_ulid ?? userData?.user_organizations?.[0]?.ulid;
    const orgUlid = resolveOrgUlid(role, ownOrgUlid, orgOverride);
    const shouldFetchFlags = shouldFetchKnotFlags(role, orgUlid, orgOverride);

    const [flags, setFlags] = useState(null);
    const [loaded, setLoaded] = useState(!shouldFetchFlags);
    const fetchGenRef = useRef(0);

    const refetch = useCallback(async () => {
        await fetchKnotFlagsForOrg({ shouldFetchFlags, orgUlid, role, fetchGenRef, setLoaded, setFlags });
    }, [shouldFetchFlags, orgUlid, role]);

    useEffect(() => {
        if (fetchOnMount) {
            refetch();
        }
    }, [refetch, fetchOnMount]);

    const visibility = useMemo(
        () => resolveKnotTabVisibility({ role, orgOverride, shouldFetchFlags, flags, userData }),
        [shouldFetchFlags, flags, userData, role, orgOverride],
    );

    const isVisible = useCallback((tab) => isKnotTabVisible(visibility, tab), [visibility]);

    const getDefaultDashboardType = useCallback(
        () => getDefaultKnotDashboardType(visibility),
        [visibility],
    );

    const resolveRedirect = useCallback(
        (currentType) => resolveKnotDashboardRedirect(currentType, visibility, loaded),
        [visibility, loaded],
    );

    return {
        visibility,
        flags: applyRoleIamFlagOverrides(flags, role),
        loaded,
        loading: shouldFetchFlags && !loaded,
        hasAnyAccess: hasAnyKnotTabAccess(visibility),
        isVisible,
        getDefaultDashboardType,
        resolveRedirect,
        refetch,
    };
}
